import { useEffect, useCallback, useRef, useContext, useState } from 'react'
import config from 'constants/config'
import { connect } from 'react-redux'
import { replace } from 'connected-react-router'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { jwtDecode } from 'jwt-decode'
import { GSILoadComplete, loginGoogle } from 'actions/AuthActions'
import { fireInteractionEvent } from 'api/googleTagManager'
import noop from 'lib/function/noop'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import MasterModalContext from 'contexts/MasterModalContext'
import useScreenSize from 'hooks/useScreenSize'
import {
  BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_CALLBACK_SOURCE,
  loginGoogleForBusinessSignup,
  BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_CALLBACK_SOURCE,
  loginGoogleForEmployeeSetup,
  getPersonalAccountLinkingGoogleAction,
} from 'actions/businessTraveller/loginGoogle'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const delayValue = 20000

interface Props {
  isLoggedIn: boolean;
  onPromptOpen?: () => void;
  onPromptClose?: () => void;
  disableTapLogin: boolean;
  callbackSource?: string;
}

interface GoogleToken {
  email: string;
  family_name: string;
  given_name: string;
  sub: string;
}

interface GoogleInitializeData {
  use_fedcm: boolean;
  client_id: string;
  auto_select?: boolean;
  callback?: (response: unknown) => void;
  ux_mode?: string;
  login_uri?: string;
}

/** https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification */
interface PromptMomentNotification {
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => 'credential_returned' | 'cancel_called' | 'flow_restarted';
  getMomentType: () => 'display' | 'skipped' | 'dismissed';
}

declare global {
  interface Window {
    onGoogleLibraryLoad: () => void;
    google: {
      accounts: {
        id: {
          cancel: () => unknown;
          disableAutoSelect: () => unknown;
          initialize: (data: GoogleInitializeData) => unknown;
          renderButton: (document: HTMLElement, props?: any) => unknown;
          prompt: (listener?: (notification: PromptMomentNotification) => void) => unknown;
        }
      }
    };
  }
}

function GoogleAuth(props: Props): null {
  const {
    isLoggedIn,
    onPromptClose = noop,
    disableTapLogin,
    callbackSource,
  } = props

  const urlSearchParams = useQueryParams()
  const { open: anyModalOpen } = useContext(MasterModalContext)

  // Use these callbacks via refs so they won't trigger the `useEffect` to re-run if they change
  const onCloseRef = useRef(onPromptClose)
  onCloseRef.current = onPromptClose

  const timeoutState = useRef<number | undefined>(undefined)
  const [hasDismissed, setHasDismissed] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const mountTime = useRef<number>(new Date().getTime())
  const isMobileSize = useScreenSize() === 'mobile'
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const whiteLabelAppConfig = useWhiteLabelAppConfig()

  const onGoogleSignedIn = useCallback((response: any) => {
    if (!response?.credential) {
      window.google.accounts.id.disableAutoSelect()
      throw new Error('Google Login error')
    }

    const googleData: GoogleToken = jwtDecode(response.credential)

    // Get callback source from global variable set by social buttons
    const callbackSourceFromGlobal = (window as any).socialLoginCallbackSource
    const finalCallbackSource = callbackSource || callbackSourceFromGlobal

    const data = {
      id_google: googleData.sub,
      email: googleData.email,
      token_id: response.credential,
      givenName: googleData.given_name,
      surname: googleData.family_name
    }

    fireInteractionEvent({
      category: response.select_by === 'auto' ? 'google-one-tap-login' : 'google-login',
      action: 'clicked',
      label: (new Date().getTime() - mountTime.current).toString(),
    })

    if (finalCallbackSource === BUSINESS_TRAVELLER_BUSINESS_SIGNUP_GOOGLE_LINK_ACCOUNT_CALLBACK_SOURCE) {
      dispatch(loginGoogleForBusinessSignup(data))
    } else if (finalCallbackSource === BUSINESS_TRAVELLER_EMPLOYEE_SETUP_GOOGLE_CALLBACK_SOURCE && isDedicatedTenantRegistrationEnabled) {
      dispatch(loginGoogleForEmployeeSetup(data))
    } else if (finalCallbackSource === 'business-traveller-personal-account-linking') {
      dispatch(getPersonalAccountLinkingGoogleAction(isDedicatedTenantRegistrationEnabled)(data))
    } else {
      dispatch(loginGoogle(data))
    }
  }, [dispatch, callbackSource, isDedicatedTenantRegistrationEnabled])

  const isLoggedInRef = useRef(isLoggedIn)
  isLoggedInRef.current = isLoggedIn

  useEffect(() => {
    if (!window.google?.accounts?.id) return
    if (disableTapLogin || anyModalOpen) {
      if (timeoutState.current) clearTimeout(timeoutState.current)
      window.google.accounts.id.cancel()
    }
  }, [anyModalOpen, disableTapLogin])

  useEffect(() => {
    const onGoogleLoad = (whiteLabelAppGoogleLoginURI = '') => {
      dispatch(GSILoadComplete())

      // For review/canary apps using shared auth domain, don't initialize Google here.
      if (config.GOOGLE_AUTH_REDIRECT_DOMAIN && !whiteLabelAppGoogleLoginURI) {
        return
      }

      const params: GoogleInitializeData = {
        use_fedcm: true,
        client_id: config.GOOGLE_APP_ID,
        auto_select: true,
        ...(whiteLabelAppGoogleLoginURI ? { ux_mode: 'redirect', login_uri: whiteLabelAppGoogleLoginURI } : { callback: onGoogleSignedIn }),
      }
      window.google.accounts.id.initialize(params)

      if (config.GOOGLE_ONE_TAP_LOGIN && !disableTapLogin && !anyModalOpen && !hasDismissed) {
        // Make sure OneTap does not pop up at same time as LE log in on mobile
        timeoutState.current = window.setTimeout(() => {
          if (!isLoggedInRef.current) {
            window.google.accounts.id.prompt((notification) => {
              if (notification.isDismissedMoment() || notification.isSkippedMoment()) {
                onCloseRef.current()
              }
              if (notification.isSkippedMoment()) setHasDismissed(true)
            })
          }
        // delay for mobile initially and for both if it's to be shown again
        }, isMobileSize || timeoutState.current ? delayValue : 0)
      }
    }

    // Handle credential from URL for redirect flows (white-label apps and review/canary apps)
    if (whiteLabelAppConfig.googleLoginURI || config.GOOGLE_AUTH_REDIRECT_DOMAIN) {
      const windowParams = new URLSearchParams(window.location.search)
      const credential = windowParams.get('credential')
      if (credential) {
        // Remove credential from URL FIRST to prevent JWT leaking via Referer, browser history, or bookmarks.
        windowParams.delete('credential')
        const cleanSearch = windowParams.toString()
        dispatch(replace(window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '')))
        onGoogleSignedIn({ credential })
        return
      }
    }

    if (window.google?.accounts) {
      // must already be loaded by the time we've mounted, don't need to hook into load function
      onGoogleLoad(whiteLabelAppConfig.googleLoginURI)
    } else {
      // not loaded yet, hook into the callback
      window.onGoogleLibraryLoad = onGoogleLoad
    }
    return () => clearTimeout(timeoutState.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onGoogleSignedIn, disableTapLogin, isMobileSize, urlSearchParams, whiteLabelAppConfig.googleLoginURI, anyModalOpen])

  return null
}

const mapState = (state: App.State) => ({
  disableTapLogin: state.utm.medium === 'cpc',
})

export default connect(mapState)(GoogleAuth)
